

import React, {

  useCallback,

  useEffect,

  useMemo,

  useRef,

  useState,

} from "react";

import { Link, useNavigate, useSearchParams } from "react-router-dom";

import {

  RoleDetails,

  UpdateRolePermissions,
  CreateClientRole,
  UpdateClientRole,

} from "../../api/Role";

import {

  LoadingDiv,

  SweetAlertShow,

} from "../master/Master";

import Header from "../master/SideBar";

import Nav from "../master/NavBar";



const toId = (value) => String(value ?? "");



const text = (value, fallback = "") =>

  typeof value === "string" && value.trim()

    ? value.trim()

    : fallback;



const unique = (values) => [...new Set(values.map(toId))];



const routeIdsOf = (item) =>

  unique(

    (item?.categories || []).flatMap((category) =>

      (category?.routes || []).map((route) => route.id)

    )

  );



const selectedCount = (ids, selected) =>

  ids.filter((id) => selected.has(id)).length;



const accessState = (ids, selected) => {

  const count = selectedCount(ids, selected);

  if (!count) return "none";

  if (count === ids.length) return "full";

  return "partial";

};



function AccessCheckbox({

  checked,

  partial = false,

  onChange,

  label,

  disabled = false,

}) {

  const ref = useRef(null);



  useEffect(() => {

    if (ref.current) {

      ref.current.indeterminate = partial;

    }

  }, [partial]);



  return (

    <input

      ref={ref}

      type="checkbox"

      className="rav-checkbox"

      checked={checked}

      onChange={(event) => onChange(event.target.checked)}

      aria-label={label}

      disabled={disabled}

    />

  );

}



export default function RoleView() {

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [roleModal, setRoleModal] = useState(false);
  const [roleName, setRoleName] = useState("");
  const [roleBusy, setRoleBusy] = useState(false);

  const id = searchParams.get("id");



  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [role, setRole] = useState(null);

  const [error, setError] = useState("");



  const [selected, setSelected] = useState(new Set());
  const [defaultIds, setDefaultIds] = useState(new Set());

  const [initialSelected, setInitialSelected] = useState(new Set());



  const [search, setSearch] = useState("");

  const [accessFilter, setAccessFilter] = useState("all");

  const [managementFilter, setManagementFilter] = useState("all");



  const [expanded, setExpanded] = useState({});

  const [expandedCategories, setExpandedCategories] = useState({});



  const [showChangesOnly, setShowChangesOnly] = useState(false);

  const [confirmClear, setConfirmClear] = useState(false);



  const savingRef = useRef(false);

  const dirtyRef = useRef(false);



  const managements = useMemo(

    () => Array.isArray(role?.managements)

      ? role.managements

      : [],

    [role]

  );



  const allRouteIds = useMemo(

    () => unique(managements.flatMap(routeIdsOf)),

    [managements]

  );



  const getRoleDetails = useCallback(async () => {

    if (!id) {

      setError("Role ID is missing from the URL.");

      setRole(null);

      return;

    }



    setLoading(true);

    setError("");



    try {

      const response = await RoleDetails(id);

      const payload = response?.data;



      if (payload?.status !== true || !payload?.data) {

        throw new Error(

          Array.isArray(payload?.message)

            ? payload.message.join(", ")

            : payload?.message || "Unable to load role details."

        );

      }



      const data = payload.data;



      if (!Array.isArray(data.managements)) {

        throw new Error(

          "The role API has not returned managements. " +

          "Please deploy the updated role_details endpoint."

        );

      }



      const availableIds = new Set(

        unique(data.managements.flatMap(routeIdsOf))

      );



      const assignedIds = unique(

        data.assigned_route_ids ||

          data.managements.flatMap((management) =>

            (management.categories || []).flatMap((category) =>

              (category.routes || [])

                .filter((route) => route.assigned)

                .map((route) => route.id)

            )

          )

      ).filter((routeId) => availableIds.has(routeId));



      const assigned = new Set(assignedIds);



      setDefaultIds(new Set(unique(data.default_route_ids || [])));
    setRole(data);

      setSelected(new Set(assigned));

      setInitialSelected(new Set(assigned));

      setExpanded({});

      setExpandedCategories({});

      setShowChangesOnly(false);

    } catch (err) {

      const message =

        err?.response?.data?.message ||

        err?.message ||

        "Unable to load role details.";



      const readable = Array.isArray(message)

        ? message.join(", ")

        : String(message);



      setError(readable);

      SweetAlertShow("error", "Error", readable);

    } finally {

      setLoading(false);

    }

  }, [id]);



  useEffect(() => {

    getRoleDetails();

  }, [getRoleDetails]);



  const dirty = useMemo(() => {

    if (selected.size !== initialSelected.size) return true;

    return [...selected].some((id) => !initialSelected.has(id));

  }, [selected, initialSelected]);



  useEffect(() => {

    dirtyRef.current = dirty;

  }, [dirty]);



  useEffect(() => {

    const onBeforeUnload = (event) => {

      if (dirtyRef.current && !savingRef.current) {

        event.preventDefault();

        event.returnValue = "";

      }

    };



    window.addEventListener("beforeunload", onBeforeUnload);

    return () =>

      window.removeEventListener("beforeunload", onBeforeUnload);

  }, []);



  const toggleRoutes = useCallback((routeIds, enabled) => {

    setSelected((previous) => {

      const next = new Set(previous);



      routeIds.forEach((routeId) => {

        const key = toId(routeId);

        if (enabled) next.add(key);

        else if (!defaultIds.has(key)) next.delete(key);

      });



      return next;

    });

  }, [defaultIds]);



  const toggleManagement = (management, enabled) => {

    toggleRoutes(routeIdsOf(management), enabled);



    if (enabled) {

      setExpanded((previous) => ({

        ...previous,

        [management.id]: true,

      }));

    }

  };



  const toggleCategory = (category, enabled) => {

    toggleRoutes(

      (category.routes || []).map((route) => route.id),

      enabled

    );

  };



  const toggleRoute = (routeId, enabled) => {

    toggleRoutes([routeId], enabled);

  };



  const stats = useMemo(() => {

    let assignedManagements = 0;

    let assignedCategories = 0;

    let fullManagements = 0;

    let partialManagements = 0;



    managements.forEach((management) => {

      const ids = routeIdsOf(management);

      const state = accessState(ids, selected);



      if (state !== "none") assignedManagements++;

      if (state === "full") fullManagements++;

      if (state === "partial") partialManagements++;



      (management.categories || []).forEach((category) => {

        const categoryIds = (category.routes || []).map(

          (route) => toId(route.id)

        );



        if (selectedCount(categoryIds, selected) > 0) {

          assignedCategories++;

        }

      });

    });



    return {

      assignedManagements,

      assignedCategories,

      fullManagements,

      partialManagements,

      assignedRoutes: selectedCount(allRouteIds, selected),

      unassignedManagements:

        managements.length - assignedManagements,

    };

  }, [managements, selected, allRouteIds]);



  const changedRouteIds = useMemo(() => {

    const changes = new Set();



    allRouteIds.forEach((routeId) => {

      if (selected.has(routeId) !== initialSelected.has(routeId)) {

        changes.add(routeId);

      }

    });



    return changes;

  }, [allRouteIds, selected, initialSelected]);



  const visibleManagements = useMemo(() => {

    const term = search.trim().toLowerCase();



    return managements

      .filter((management) => {

        if (

          managementFilter !== "all" &&

          toId(management.id) !== managementFilter

        ) {

          return false;

        }



        const ids = routeIdsOf(management);

        const state = accessState(ids, selected);



        if (accessFilter === "assigned" && state === "none") {

          return false;

        }



        if (accessFilter === "unassigned" && state !== "none") {

          return false;

        }



        if (accessFilter === "partial" && state !== "partial") {

          return false;

        }



        if (

          accessFilter === "full" &&

          state !== "full"

        ) {

          return false;

        }



        if (

          showChangesOnly &&

          !ids.some((routeId) => changedRouteIds.has(routeId))

        ) {

          return false;

        }



        if (!term) return true;



        const searchable = [

          management.name,

          ...(management.categories || []).flatMap((category) => [

            category.name,

            ...(category.routes || []).flatMap((route) => [

              route.name,

              route.frontend_url,

              route.backend_url,

              route.app_url,

            ]),

          ]),

        ]

          .filter(Boolean)

          .join(" ")

          .toLowerCase();



        return searchable.includes(term);

      })

      .sort((a, b) =>

        String(a.name).localeCompare(String(b.name))

      );

  }, [

    managements,

    managementFilter,

    accessFilter,

    selected,

    search,

    showChangesOnly,

    changedRouteIds,

  ]);



  const resetFilters = () => {

    setSearch("");

    setAccessFilter("all");

    setManagementFilter("all");

    setShowChangesOnly(false);

  };



  const discardChanges = () => {

    setSelected(new Set(initialSelected));

  };



  const savePermissions = async () => {

    if (!id || !role || saving) return;



    setSaving(true);

    savingRef.current = true;



    try {

      const routeIds = allRouteIds

        .filter((routeId) => selected.has(routeId) || defaultIds.has(routeId))

        .map(Number);



      const response = await UpdateRolePermissions(id, routeIds);

      const payload = response?.data;



      if (payload?.status !== true) {

        throw new Error(

          Array.isArray(payload?.message)

            ? payload.message.join(", ")

            : payload?.message || "Unable to save permissions."

        );

      }



      SweetAlertShow(

        "success",

        "Success",

        payload?.message || "Role permissions updated successfully."

      );



      await getRoleDetails();

    } catch (err) {

      const message =

        err?.response?.data?.message ||

        err?.message ||

        "Unable to save permissions.";



      SweetAlertShow(

        "error",

        "Error",

        Array.isArray(message) ? message.join(", ") : String(message)

      );

    } finally {

      setSaving(false);

      savingRef.current = false;

    }

  };



  const submitRole = async (event) => {
    event.preventDefault();
    if (!roleName.trim() || roleBusy) return;
    setRoleBusy(true);
    try {
      const response = role?.is_default === false && roleModal === "edit"
        ? await UpdateClientRole(role.id, roleName.trim())
        : await CreateClientRole(roleName.trim());
      if (response?.data?.status !== true) throw new Error(response?.data?.message || "Could not save role");
      setRoleModal(false);
      setRoleName("");
      SweetAlertShow("success", "Success", response.data.message || "Role saved");
      const newId = response.data.data?.id;
      if (newId) navigate(`/role/view?id=${newId}`);
      else await getRoleDetails();
    } catch (error) {
      SweetAlertShow("error", "Error", String(error?.response?.data?.message || error.message));
    } finally { setRoleBusy(false); }
  };

  const totalCategories = managements.reduce(

    (sum, management) =>

      sum + (management.categories || []).length,

    0

  );



  const unavailableCount =

    Number(role?.summary?.unavailable_assigned_routes || 0);



  return (

    <>

      {(loading || saving) && <LoadingDiv />}



      <div className="layout-wrapper layout-content-navbar rav-root">

        <div className="layout-container">

          <Header />



          <div className="layout-page">

            <Nav />



            <div className="content-wrapper">

              <div className="container-fluid flex-grow-1 container-p-y rav-page">



                <div className="rav-top">

                  <div>

                    <div className="rav-eyebrow">

                      ADMINISTRATION / ROLE MANAGEMENT

                    </div>



                    <h4>

                      <i className="ri-shield-user-line" />

                      {" "}

                      {role?.name || "Role Access"}

                    </h4>



                    <p>

                      Manage access by management, category,

                      or individual route.

                    </p>

                  </div>



                  <div className="rav-top-actions">
                    {/* <button type="button" className="btn btn-success btn-sm" onClick={() => { setRoleModal("create"); setRoleName(""); }}>
                      <i className="ri-add-line me-1" /> Create Custom Role
                    </button> */}
                    {role && !role.is_default && <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => {setRoleName(role.name || ""); setRoleModal("edit");}}>Rename Role</button>}


                    <button

                      className="btn btn-outline-primary btn-sm"

                      onClick={getRoleDetails}

                      disabled={loading || saving || dirty}

                      type="button"

                      title={

                        dirty

                          ? "Save or discard changes before refreshing"

                          : "Refresh"

                      }

                    >

                      <i className="ri-refresh-line me-1" />

                      Refresh

                    </button>



                    <Link

                      to="/role"

                      className="btn btn-primary btn-sm"

                      onClick={(event) => {

                        if (

                          dirty &&

                          !window.confirm(

                            "Discard unsaved permission changes?"

                          )

                        ) {

                          event.preventDefault();

                        }

                      }}

                    >

                      <i className="ri-arrow-left-line me-1" />

                      Back to Roles

                    </Link>

                  </div>

                </div>



                {error && (

                  <div className="alert alert-danger py-2">

                    {error}

                  </div>

                )}



                {!role && !loading && !id && <div className="card p-4 text-center"><h5>Create a client role</h5><p>Use Create Custom Role to begin, or return to the roles list.</p></div>}
              {role && (

                  <>

                    <div className="rav-summary">

                      <div className="rav-stat">

                        <span className="rav-stat-icon purple">

                          <i className="ri-apps-2-line" />

                        </span>

                        <div>

                          <strong>{stats.assignedManagements}</strong>

                          <small>of {managements.length} Managements</small>

                        </div>

                      </div>



                      <div className="rav-stat">

                        <span className="rav-stat-icon blue">

                          <i className="ri-folder-check-line" />

                        </span>

                        <div>

                          <strong>{stats.assignedCategories}</strong>

                          <small>of {totalCategories} Categories</small>

                        </div>

                      </div>



                      <div className="rav-stat">

                        <span className="rav-stat-icon green">

                          <i className="ri-checkbox-circle-line" />

                        </span>

                        <div>

                          <strong>{stats.assignedRoutes}</strong>

                          <small>of {allRouteIds.length} Routes</small>

                        </div>

                      </div>

                    </div>



                    {role.is_default && <div className="alert alert-info py-2 small mb-3">
                    <i className="ri-lock-line me-1" /> <strong>Default role:</strong> Global permissions are locked. You can add or remove only your client's additional permissions. Locked routes stay checked.
                  </div>}
                  {!role.is_default && <div className="alert alert-success py-2 small mb-3">Custom role for this client: all its route permissions are editable.</div>}
                  <section className="rav-card">

                      <div className="rav-card-header">

                        <div>

                          <h5>Management Access</h5>

                          <p>

                            Select a management to grant all access.

                            Expand it to customize categories.

                          </p>

                        </div>



                        <div className="rav-bulk-actions">

                          <button

                            type="button"

                            className="btn btn-outline-primary btn-sm"

                            onClick={() =>

                              toggleRoutes(allRouteIds, true)

                            }

                          >

                            Select All

                          </button>



                          <button

                            type="button"

                            className="btn btn-outline-secondary btn-sm"

                            onClick={() => setConfirmClear(true)}

                          >

                            Clear All

                          </button>

                        </div>

                      </div>



                      <div className="rav-filters">

                        <div className="rav-filter-grid">

                          <label>

                            <span>Search</span>

                            <div className="rav-search-input">

                              <i className="ri-search-line" />

                              <input

                                type="search"

                                className="form-control form-control-sm"

                                placeholder="Management, category, route..."

                                value={search}

                                onChange={(event) =>

                                  setSearch(event.target.value)

                                }

                              />

                            </div>

                          </label>



                          <label>

                            <span>Management</span>

                            <select

                              className="form-select form-select-sm"

                              value={managementFilter}

                              onChange={(event) =>

                                setManagementFilter(event.target.value)

                              }

                            >

                              <option value="all">All Managements</option>

                              {managements.map((management) => (

                                <option

                                  key={management.id}

                                  value={toId(management.id)}

                                >

                                  {management.name}

                                </option>

                              ))}

                            </select>

                          </label>

                        </div>



                        <div className="rav-filter-bottom">

                          <div className="rav-filter-tabs">

                            {[

                              ["all", "All", managements.length],

                              [

                                "assigned",

                                "Assigned",

                                stats.assignedManagements,

                              ],

                              [

                                "unassigned",

                                "Not Assigned",

                                stats.unassignedManagements,

                              ],

                              [

                                "partial",

                                "Partial",

                                stats.partialManagements,

                              ],

                              [

                                "full",

                                "Full Access",

                                stats.fullManagements,

                              ],

                            ].map(([value, label, count]) => (

                              <button

                                type="button"

                                key={value}

                                className={

                                  "rav-filter-tab " +

                                  (accessFilter === value

                                    ? "active"

                                    : "")

                                }

                                onClick={() => setAccessFilter(value)}

                              >

                                {label}

                                <span>{count}</span>

                              </button>

                            ))}

                          </div>



                          <div className="rav-filter-extras">

                            <label className="rav-changes-label">

                              <input

                                type="checkbox"

                                checked={showChangesOnly}

                                onChange={(event) =>

                                  setShowChangesOnly(

                                    event.target.checked

                                  )

                                }

                              />

                              Changed Only

                            </label>



                            <button

                              type="button"

                              className="rav-link"

                              onClick={resetFilters}

                            >

                              Reset

                            </button>

                          </div>

                        </div>

                      </div>



                      <div className="rav-list-header">

                        <span>

                          {visibleManagements.length} managements shown

                        </span>



                        <div>

                          <button

                            type="button"

                            onClick={() =>

                              setExpanded(

                                Object.fromEntries(

                                  managements.map((m) => [

                                    m.id,

                                    true,

                                  ])

                                )

                              )

                            }

                          >

                            Expand All

                          </button>



                          <button

                            type="button"

                            onClick={() => setExpanded({})}

                          >

                            Collapse All

                          </button>

                        </div>

                      </div>



                      <div className="rav-management-list">

                        {visibleManagements.map((management) => {

                          const managementIds =

                            routeIdsOf(management);



                          const count = selectedCount(

                            managementIds,

                            selected

                          );



                          const state = accessState(

                            managementIds,

                            selected

                          );



                          const isOpen =

                            !!expanded[management.id];



                          const categoryCount =

                            (management.categories || [])

                              .filter((category) =>

                                (category.routes || []).some(

                                  (route) =>

                                    selected.has(toId(route.id))

                                )

                              ).length;



                          return (

                            <div

                              className={

                                "rav-management " +

                                (isOpen ? "open" : "")

                              }

                              key={management.id}

                            >

                              <div className="rav-management-head">

                                <AccessCheckbox

                                  checked={state === "full"}

                                  partial={state === "partial"}

                                  label={

                                    "Select all " + management.name

                                  }

                                  onChange={(checked) =>

                                    toggleManagement(

                                      management,

                                      checked

                                    )

                                  }

                                />



                                <button

                                  type="button"

                                  className="rav-management-title"

                                  aria-expanded={isOpen}

                                  onClick={() =>

                                    setExpanded((previous) => ({

                                      ...previous,

                                      [management.id]: !isOpen,

                                    }))

                                  }

                                >

                                  <span className="rav-management-icon">

                                    <i className="ri-folder-shield-2-line" />

                                  </span>



                                  <span>

                                    <strong>{management.name}</strong>

                                    <small>

                                      {categoryCount}/

                                      {(management.categories || []).length}

                                      {" "}categories · {count}/

                                      {managementIds.length} routes

                                    </small>

                                  </span>

                                </button>



                                <span

                                  className={

                                    "rav-access-badge " + state

                                  }

                                >

                                  {state === "full"

                                    ? "Full Access"

                                    : state === "partial"

                                    ? "Partial Access"

                                    : "Not Assigned"}

                                </span>



                                <button

                                  type="button"

                                  className="rav-chevron"

                                  aria-label={

                                    isOpen

                                      ? "Collapse management"

                                      : "Expand management"

                                  }

                                  onClick={() =>

                                    setExpanded((previous) => ({

                                      ...previous,

                                      [management.id]: !isOpen,

                                    }))

                                  }

                                >

                                  <i

                                    className={

                                      isOpen

                                        ? "ri-arrow-up-s-line"

                                        : "ri-arrow-down-s-line"

                                    }

                                  />

                                </button>

                              </div>



                              {isOpen && (

                                <div className="rav-categories">

                                  {(management.categories || []).map(

                                    (category) => {

                                      const categoryIds =

                                        (category.routes || []).map(

                                          (route) => toId(route.id)

                                        );



                                      const categoryState =

                                        accessState(

                                          categoryIds,

                                          selected

                                        );



                                      const categorySelected =

                                        selectedCount(

                                          categoryIds,

                                          selected

                                        );



                                      const categoryKey =

                                        management.id +

                                        ":" +

                                        category.id;



                                      const routesOpen =

                                        !!expandedCategories[

                                          categoryKey

                                        ];



                                      return (

                                        <div

                                          className="rav-category"

                                          key={categoryKey}

                                        >

                                          <div className="rav-category-head">

                                            <AccessCheckbox

                                              checked={

                                                categoryState === "full"

                                              }

                                              partial={

                                                categoryState === "partial"

                                              }

                                              label={

                                                "Select " +

                                                category.name

                                              }

                                              onChange={(checked) =>

                                                toggleCategory(

                                                  category,

                                                  checked

                                                )

                                              }

                                            />



                                            <span className="rav-category-name">

                                              {category.name}

                                            </span>



                                            <span

                                              className={

                                                "rav-category-count " +

                                                categoryState

                                              }

                                            >

                                              {categorySelected}/

                                              {categoryIds.length}

                                            </span>



                                            <button

                                              type="button"

                                              className="rav-customize"

                                              aria-expanded={routesOpen}

                                              onClick={() =>

                                                setExpandedCategories(

                                                  (previous) => ({

                                                    ...previous,

                                                    [categoryKey]:

                                                      !routesOpen,

                                                  })

                                                )

                                              }

                                            >

                                              {routesOpen

                                                ? "Hide Routes"

                                                : "Customize Routes"}

                                              <i

                                                className={

                                                  routesOpen

                                                    ? "ri-arrow-up-s-line"

                                                    : "ri-arrow-down-s-line"

                                                }

                                              />

                                            </button>

                                          </div>



                                          {routesOpen && (

                                            <div className="rav-routes">

                                              {(category.routes || []).map(

                                                (route) => {

                                                  const routeId =

                                                    toId(route.id);



                                                  const granted =

                                                    selected.has(

                                                      routeId

                                                    );



                                                  const routeChanged =

                                                    granted !==

                                                    initialSelected.has(

                                                      routeId

                                                    );



                                                  return (

                                                    <label

                                                      className={

                                                        "rav-route " +

                                                        (granted

                                                          ? "granted "

                                                          : "") +

                                                        (routeChanged

                                                          ? "changed"

                                                          : "")

                                                      }

                                                      key={routeId}

                                                    >

                                                      <AccessCheckbox

                                                        checked={

                                                          granted

                                                        }

                                                        label={

                                                          route.name

                                                        }

                                                        onChange={(

                                                          checked

                                                        ) =>

                                                          toggleRoute(

                                                            routeId,

                                                            checked

                                                          )

                                                        }

                                                      />



                                                      <span>

                                                        <strong>

                                                          {text(

                                                            route.name,

                                                            "Unnamed Route"

                                                          )}

                                                        </strong>

                                                        <small>

                                                          {route.frontend_url ||

                                                            route.backend_url ||

                                                            route.app_url ||

                                                            "Route permission"}

                                                        </small>

                                                      </span>



                                                      {defaultIds.has(routeId) && <span className="badge bg-label-secondary ms-2" title="Global default access cannot be removed">Default · Locked</span>}
                                                     {!defaultIds.has(routeId) && granted && role?.is_default && <span className="badge bg-label-success ms-2">Client Extra</span>}
                                                     {routeChanged && (

                                                        <i

                                                          className="ri-edit-circle-line rav-changed-icon"

                                                          title="Unsaved change"

                                                        />

                                                      )}

                                                    </label>

                                                  );

                                                }

                                              )}

                                            </div>

                                          )}

                                        </div>

                                      );

                                    }

                                  )}

                                </div>

                              )}

                            </div>

                          );

                        })}



                        {!loading &&

                          visibleManagements.length === 0 && (

                            <div className="rav-empty">

                              <i className="ri-search-eye-line" />

                              <strong>No matching managements</strong>

                              <small>

                                Try changing the search or access filters.

                              </small>

                              <button

                                type="button"

                                className="btn btn-outline-primary btn-sm"

                                onClick={resetFilters}

                              >

                                Clear Filters

                              </button>

                            </div>

                          )}

                      </div>

                    </section>



                    {unavailableCount > 0 && (

                      <div className="alert alert-warning small">

                        {unavailableCount} previously assigned route

                        permissions are outside the active management

                        list. They are not editable here and will be

                        preserved by the update endpoint.

                      </div>

                    )}



                    <div className="rav-save-bar">

                      <div>

                        <strong>

                          {stats.assignedRoutes} / {allRouteIds.length}

                          {" "}routes selected

                        </strong>



                        <small className={dirty ? "dirty" : ""}>

                          {dirty

                            ? `${changedRouteIds.size} unsaved route changes`

                            : "All changes saved"}

                        </small>

                      </div>



                      <div className="rav-save-actions">

                        {dirty && (

                          <button

                            type="button"

                            className="btn btn-outline-secondary btn-sm"

                            onClick={discardChanges}

                            disabled={saving}

                          >

                            Discard

                          </button>

                        )}



                        <button

                          type="button"

                          className="btn btn-primary btn-sm"

                          disabled={!dirty || saving}

                          onClick={savePermissions}

                        >

                          <i className="ri-save-line me-1" />

                          {saving

                            ? "Saving..."

                            : "Save Permissions"}

                        </button>

                      </div>

                    </div>

                  </>

                )}

              </div>

            </div>

          </div>

        </div>

      </div>



      {roleModal && (
      <div className="rav-dialog-backdrop">
        <form className="rav-dialog" onSubmit={submitRole} role="dialog" aria-modal="true">
          <h5>{roleModal === "edit" ? "Rename Custom Role" : "Create Custom Role"}</h5>
          <p>{roleModal === "edit" ? "Only your own client role can be renamed." : "Create a client-only role, then select managements and permissions."}</p>
          <input className="form-control" maxLength={255} required minLength={2} autoFocus value={roleName} onChange={e => setRoleName(e.target.value)} placeholder="e.g. Reception Supervisor" />
          <div className="rav-dialog-actions mt-3">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRoleModal(false)} disabled={roleBusy}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={roleBusy || roleName.trim().length < 2}>{roleBusy ? "Saving..." : "Save Role"}</button>
          </div>
        </form>
      </div>
    )}
    {confirmClear && (

        <div className="rav-dialog-backdrop">

          <div

            className="rav-dialog"

            role="dialog"

            aria-modal="true"

            aria-labelledby="rav-clear-title"

          >

            <h5 id="rav-clear-title">Remove all permissions?</h5>

            <p>

              This will uncheck every editable route for this role.

              The changes will not be applied until you save.

            </p>



            <div className="rav-dialog-actions">

              <button

                type="button"

                className="btn btn-outline-secondary btn-sm"

                onClick={() => setConfirmClear(false)}

              >

                Cancel

              </button>



              <button

                type="button"

                className="btn btn-danger btn-sm"

                onClick={() => {

                  toggleRoutes(allRouteIds, false);

                  setConfirmClear(false);

                }}

              >

                Clear Permissions

              </button>

            </div>

          </div>

        </div>

      )}



      <style>{`

        .rav-root {

          \--rav-border: #e5e9f0;

          \--rav-muted: #8792a3;

          \--rav-accent: #696cff;

          background: #f7f8fb;

          min-height: 100vh;

        }



        .rav-page {

          max-width: 1650px;

          margin: 0 auto;

          padding-bottom: 95px !important;

        }



        .rav-top {

          display: flex;

          justify-content: space-between;

          align-items: center;

          gap: 14px;

          flex-wrap: wrap;

          margin-bottom: 14px;

        }



        .rav-eyebrow {

          font-size: 10px;

          font-weight: 800;

          letter-spacing: .07em;

          color: var(--rav-accent);

        }



        .rav-top h4 {

          margin: 5px 0 4px;

          font-size: 21px;

          font-weight: 800;

          color: #303746;

        }



        .rav-top h4 i {

          color: var(--rav-accent);

        }



        .rav-top p {

          font-size: 12px;

          color: var(--rav-muted);

          margin: 0;

        }



        .rav-top-actions {

          display: flex;

          gap: 8px;

        }



        .rav-summary {

          display: grid;

          grid-template-columns: repeat(3, minmax(0, 1fr));

          gap: 12px;

          margin-bottom: 14px;

        }



        .rav-stat {

          display: flex;

          align-items: center;

          gap: 13px;

          min-width: 0;

          padding: 14px 16px;

          background: #fff;

          border: 1px solid var(--rav-border);

          border-radius: 12px;

        }



        .rav-stat-icon {

          width: 42px;

          height: 42px;

          flex-shrink: 0;

          display: grid;

          place-items: center;

          font-size: 20px;

          border-radius: 10px;

        }



        .rav-stat-icon.purple {

          color: #696cff;

          background: #eeeefe;

        }



        .rav-stat-icon.blue {

          color: #2381c7;

          background: #eaf4ff;

        }



        .rav-stat-icon.green {

          color: #20945b;

          background: #e9f8f0;

        }



        .rav-stat strong {

          display: block;

          font-size: 23px;

          font-weight: 800;

          color: #303746;

          line-height: 1.2;

        }



        .rav-stat small {

          display: block;

          font-size: 11px;

          color: var(--rav-muted);

          margin-top: 3px;

        }



        .rav-card {

          background: #fff;

          border: 1px solid var(--rav-border);

          border-radius: 13px;

          overflow: hidden;

          margin-bottom: 14px;

        }



        .rav-card-header {

          padding: 16px 18px;

          display: flex;

          align-items: center;

          justify-content: space-between;

          gap: 12px;

          flex-wrap: wrap;

          border-bottom: 1px solid var(--rav-border);

        }



        .rav-card-header h5 {

          font-size: 15px;

          font-weight: 800;

          margin: 0 0 4px;

        }



        .rav-card-header p {

          margin: 0;

          font-size: 11px;

          color: var(--rav-muted);

        }



        .rav-bulk-actions {

          display: flex;

          gap: 7px;

        }



        .rav-filters {

          padding: 14px 17px;

          background: #fbfcfe;

          border-bottom: 1px solid var(--rav-border);

        }



        .rav-filter-grid {

          display: grid;

          grid-template-columns: minmax(0, 2fr) minmax(160px, 1fr);

          gap: 12px;

        }



        .rav-filter-grid label {

          min-width: 0;

        }



        .rav-filter-grid label > span {

          display: block;

          margin-bottom: 5px;

          font-size: 11px;

          font-weight: 700;

          color: #788396;

        }



        .rav-filter-grid .form-control,

        .rav-filter-grid .form-select {

          min-height: 38px;

          border-radius: 8px;

          font-size: 12px;

          background-color: #fff;

        }



        .rav-search-input {

          position: relative;

        }



        .rav-search-input > i {

          position: absolute;

          top: 50%;

          left: 12px;

          transform: translateY(-50%);

          color: #9ca5b4;

        }



        .rav-search-input input {

          padding-left: 35px;

        }



        .rav-filter-bottom {

          display: flex;

          justify-content: space-between;

          align-items: center;

          gap: 10px;

          flex-wrap: wrap;

          margin-top: 13px;

        }



        .rav-filter-tabs {

          display: flex;

          gap: 7px;

          flex-wrap: wrap;

        }



        .rav-filter-tab {

          display: inline-flex;

          align-items: center;

          gap: 6px;

          padding: 7px 11px;

          border: 1px solid var(--rav-border);

          border-radius: 8px;

          background: #fff;

          color: #687587;

          font-size: 11px;

          cursor: pointer;

        }



        .rav-filter-tab.active {

          background: #eeeefe;

          color: #696cff;

          border-color: #dadaff;

          font-weight: 800;

        }



        .rav-filter-tab span {

          background: rgba(125, 135, 155, .12);

          padding: 2px 6px;

          border-radius: 6px;

          font-size: 10px;

        }



        .rav-filter-extras {

          display: flex;

          align-items: center;

          gap: 12px;

        }



        .rav-changes-label {

          display: flex;

          align-items: center;

          gap: 6px;

          font-size: 11px;

          color: #657185;

          margin: 0;

          cursor: pointer;

        }



        .rav-changes-label input {

          accent-color: #696cff;

        }



        .rav-link {

          border: 0;

          background: transparent;

          color: #696cff;

          font-size: 11px;

          font-weight: 700;

          cursor: pointer;

        }



        .rav-list-header {

          display: flex;

          justify-content: space-between;

          align-items: center;

          gap: 10px;

          flex-wrap: wrap;

          padding: 11px 17px;

          font-size: 11px;

          color: #8994a4;

        }



        .rav-list-header > div {

          display: flex;

          gap: 12px;

        }



        .rav-list-header button {

          border: 0;

          background: transparent;

          color: #696cff;

          font-size: 11px;

          cursor: pointer;

          font-weight: 700;

        }



        .rav-management-list {

          padding: 0 13px 14px;

        }



        .rav-management {

          border: 1px solid var(--rav-border);

          border-radius: 10px;

          overflow: hidden;

          margin-bottom: 9px;

          background: #fff;

        }



        .rav-management-head {

          display: flex;

          align-items: center;

          gap: 12px;

          padding: 11px 13px;

          min-height: 63px;

        }



        .rav-checkbox {

          width: 18px;

          height: 18px;

          flex-shrink: 0;

          cursor: pointer;

          accent-color: #696cff;

        }



        .rav-management-title {

          flex: 1;

          min-width: 0;

          display: flex;

          align-items: center;

          gap: 10px;

          border: 0;

          background: transparent;

          text-align: left;

          padding: 0;

          color: inherit;

          cursor: pointer;

        }



        .rav-management-icon {

          width: 35px;

          height: 35px;

          border-radius: 9px;

          background: #f0f0ff;

          color: #696cff;

          display: grid;

          place-items: center;

          font-size: 18px;

          flex-shrink: 0;

        }



        .rav-management-title strong {

          display: block;

          font-size: 13px;

          color: #3e4655;

          font-weight: 800;

          overflow-wrap: anywhere;

        }



        .rav-management-title small {

          display: block;

          margin-top: 4px;

          font-size: 10px;

          color: #939dad;

        }



        .rav-access-badge {

          padding: 5px 9px;

          border-radius: 20px;

          font-size: 10px;

          font-weight: 800;

          white-space: nowrap;

          background: #f0f2f6;

          color: #7a8595;

        }



        .rav-access-badge.full {

          background: #e5f8ee;

          color: #208657;

        }



        .rav-access-badge.partial {

          background: #fff2dc;

          color: #b58124;

        }



        .rav-chevron {

          background: transparent;

          border: 0;

          color: #8994a4;

          font-size: 20px;

          padding: 4px;

          cursor: pointer;

        }



        .rav-categories {

          padding: 5px 14px 12px 45px;

          background: #fbfcff;

          border-top: 1px solid var(--rav-border);

        }



        .rav-category {

          padding: 10px 0;

          border-bottom: 1px solid #edf0f5;

        }



        .rav-category:last-child {

          border-bottom: 0;

        }



        .rav-category-head {

          display: flex;

          align-items: center;

          gap: 10px;

        }



        .rav-category-name {

          flex: 1;

          min-width: 0;

          font-size: 12px;

          font-weight: 700;

          color: #526075;

          overflow-wrap: anywhere;

        }



        .rav-category-count {

          font-size: 10px;

          padding: 4px 7px;

          background: #f0f2f6;

          border-radius: 6px;

          color: #8792a2;

          white-space: nowrap;

        }



        .rav-category-count.full {

          color: #208657;

          background: #e5f8ee;

        }



        .rav-category-count.partial {

          color: #b58124;

          background: #fff2dc;

        }



        .rav-customize {

          display: inline-flex;

          align-items: center;

          gap: 3px;

          border: 0;

          background: transparent;

          color: #696cff;

          font-size: 11px;

          font-weight: 700;

          white-space: nowrap;

          cursor: pointer;

        }



        .rav-routes {

          display: grid;

          grid-template-columns: repeat(2, minmax(0, 1fr));

          gap: 9px;

          padding: 12px;

          margin: 11px 0 0 28px;

          background: #f3f5fa;

          border-radius: 9px;

        }



        .rav-route {

          display: flex;

          align-items: flex-start;

          gap: 8px;

          min-width: 0;

          background: #fff;

          border: 1px solid #e7eaf0;

          border-radius: 8px;

          padding: 10px;

          margin: 0;

          cursor: pointer;

        }



        .rav-route.granted {

          border-color: #cbe9d8;

          background: #fbfffd;

        }



        .rav-route.changed {

          border-color: #b6b6ff;

        }



        .rav-route > span {

          flex: 1;

          min-width: 0;

        }



        .rav-route strong {

          display: block;

          font-size: 11px;

          color: #4c5666;

          overflow-wrap: anywhere;

        }



        .rav-route small {

          display: block;

          margin-top: 3px;

          font-size: 10px;

          color: #99a4b3;

          overflow-wrap: anywhere;

        }



        .rav-changed-icon {

          color: #696cff;

        }



        .rav-empty {

          display: flex;

          flex-direction: column;

          align-items: center;

          gap: 9px;

          padding: 45px 15px;

          color: #8a94a3;

          text-align: center;

        }



        .rav-empty i {

          font-size: 29px;

        }



        .rav-empty strong {

          font-size: 13px;

          color: #4e596a;

        }



        .rav-empty small {

          font-size: 11px;

        }



        .rav-save-bar {

          position: sticky;

          bottom: 12px;

          z-index: 20;

          display: flex;

          align-items: center;

          justify-content: space-between;

          gap: 12px;

          flex-wrap: wrap;

          background: #fff;

          border: 1px solid var(--rav-border);

          border-radius: 12px;

          box-shadow: 0 6px 26px rgba(30, 40, 70, .12);

          padding: 13px 17px;

        }



        .rav-save-bar strong {

          display: block;

          font-size: 13px;

          color: #3b4555;

        }



        .rav-save-bar small {

          display: block;

          font-size: 11px;

          color: #8a95a4;

          margin-top: 3px;

        }



        .rav-save-bar small.dirty {

          color: #bd8223;

        }



        .rav-save-actions {

          display: flex;

          gap: 8px;

        }



        .rav-dialog-backdrop {

          position: fixed;

          inset: 0;

          z-index: 2000;

          display: grid;

          place-items: center;

          padding: 15px;

          background: rgba(20, 27, 45, .55);

        }



        .rav-dialog {

          width: min(100%, 430px);

          padding: 22px;

          border-radius: 13px;

          background: #fff;

          box-shadow: 0 15px 60px rgba(0,0,0,.15);

        }



        .rav-dialog h5 {

          font-size: 17px;

          font-weight: 800;

        }



        .rav-dialog p {

          color: #758093;

          font-size: 12px;

          line-height: 1.6;

        }



        .rav-dialog-actions {

          display: flex;

          justify-content: flex-end;

          gap: 8px;

        }



        @media (max-width: 900px) {

          .rav-summary {

            grid-template-columns: repeat(3, minmax(0, 1fr));

          }



          .rav-routes {

            grid-template-columns: 1fr;

          }

        }



        @media (max-width: 650px) {

          .rav-page {

            padding-left: 10px !important;

            padding-right: 10px !important;

          }



          .rav-top {

            align-items: flex-start;

            flex-direction: column;

          }



          .rav-top-actions {

            width: 100%;

          }



          .rav-top-actions > \* {

            flex: 1;

          }



          .rav-summary {

            gap: 7px;

          }



          .rav-stat {

            flex-direction: column;

            align-items: flex-start;

            gap: 7px;

            padding: 11px;

          }



          .rav-stat-icon {

            width: 32px;

            height: 32px;

            font-size: 16px;

          }



          .rav-stat strong {

            font-size: 19px;

          }



          .rav-stat small {

            font-size: 10px;

          }



          .rav-filter-grid {

            grid-template-columns: 1fr;

          }



          .rav-management-head {

            gap: 7px;

            padding: 10px;

          }



          .rav-management-icon {

            display: none;

          }



          .rav-management-title strong {

            font-size: 12px;

          }



          .rav-access-badge {

            font-size: 9px;

            padding: 4px 6px;

          }



          .rav-categories {

            padding: 5px 10px 10px;

          }



          .rav-category-head {

            gap: 7px;

          }



          .rav-customize {

            font-size: 10px;

          }



          .rav-routes {

            margin-left: 0;

          }



          .rav-save-bar {

            bottom: 7px;

          }

        }

      `}</style>

    </>

  );

}
